import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Network, RotateCcw, ZoomIn, ZoomOut, Move, MousePointer, 
  Play, Pause, Settings, Eye, EyeOff, Filter,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, RotateCw
} from 'lucide-react';
import { allMockData } from '../data/knowledgeGraph';

const KnowledgeGraphVisualization = ({ selectedSample = null }) => {
  const [selectedData, setSelectedData] = useState('quadratic');
  const [graphState, setGraphState] = useState({
    zoom: 1,
    rotation: { x: 0, y: 0, z: 0 },
    translation: { x: 0, y: 0 },
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    selectedNode: null,
    hoveredNode: null,
    isAutoRotating: false
  });

  const [graphSettings, setGraphSettings] = useState({
    nodeSize: 1.2,
    linkDistance: 120,
    showLabels: true,
    showRelationshipLabels: true,
    animate: true,
    edgeThickness: 1.5
  });

  const [filterSettings, setFilterSettings] = useState({
    concepts: { visible: true, isolated: false },
    formulas: { visible: true, isolated: false },
    values: { visible: true, isolated: false },
    steps: { visible: true, isolated: false },
    relationships: { visible: true, isolated: false }
  });

  const svgRef = useRef(null);
  const autoRotateRef = useRef(null);

  // Auto-rotation effect
  useEffect(() => {
    if (graphState.isAutoRotating) {
      autoRotateRef.current = setInterval(() => {
        setGraphState(prev => ({
          ...prev,
          rotation: {
            ...prev.rotation,
            z: (prev.rotation.z + 1) % 360
          }
        }));
      }, 50);
    } else {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
      }
    }

    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
      }
    };
  }, [graphState.isAutoRotating]);

  // Enhanced 3D transformation
  const apply3DTransform = useCallback((x, y, z = 0) => {
    const radX = (graphState.rotation.x * Math.PI) / 180;
    const radY = (graphState.rotation.y * Math.PI) / 180;
    const radZ = (graphState.rotation.z * Math.PI) / 180;

    let newX = x * Math.cos(radY) - z * Math.sin(radY);
    let newZ = x * Math.sin(radY) + z * Math.cos(radY);
    
    let newY = y * Math.cos(radX) - newZ * Math.sin(radX);
    newZ = y * Math.sin(radX) + newZ * Math.cos(radX);
    
    const finalX = newX * Math.cos(radZ) - newY * Math.sin(radZ);
    const finalY = newX * Math.sin(radZ) + newY * Math.cos(radZ);

    const perspective = 800;
    const projectedX = (finalX * perspective) / (perspective + newZ);
    const projectedY = (finalY * perspective) / (perspective + newZ);
    const scale = perspective / (perspective + newZ);

    return { 
      x: projectedX, 
      y: projectedY, 
      scale: Math.max(0.3, Math.min(2, scale)),
      depth: newZ 
    };
  }, [graphState.rotation]);

  // Graph interaction handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.node-element')) return;
    
    setGraphState(prev => ({
      ...prev,
      isDragging: true,
      dragStart: { x: e.clientX, y: e.clientY }
    }));
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (graphState.isDragging) {
      const deltaX = e.clientX - graphState.dragStart.x;
      const deltaY = e.clientY - graphState.dragStart.y;
      
      setGraphState(prev => ({
        ...prev,
        translation: {
          x: prev.translation.x + deltaX * 0.5,
          y: prev.translation.y + deltaY * 0.5
        },
        dragStart: { x: e.clientX, y: e.clientY }
      }));
    }
  }, [graphState.isDragging, graphState.dragStart]);

  const handleMouseUp = useCallback(() => {
    setGraphState(prev => ({ ...prev, isDragging: false }));
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
    setGraphState(prev => ({ 
      ...prev, 
      zoom: Math.max(0.3, Math.min(3, prev.zoom + zoomDelta))
    }));
  }, []);

  // Control functions
  const resetView = () => {
    setGraphState(prev => ({
      ...prev,
      zoom: 1,
      rotation: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0 },
      selectedNode: null,
      hoveredNode: null,
      isAutoRotating: false
    }));
  };

  const rotateGraph = (axis, degrees) => {
    setGraphState(prev => ({
      ...prev,
      rotation: {
        ...prev.rotation,
        [axis]: (prev.rotation[axis] + degrees) % 360
      }
    }));
  };

  const handleNodeClick = useCallback((node) => {
    setGraphState(prev => ({ 
      ...prev, 
      selectedNode: prev.selectedNode === node.id ? null : node.id
    }));
  }, []);

  const handleNodeHover = useCallback((node) => {
    setGraphState(prev => ({ ...prev, hoveredNode: node?.id || null }));
  }, []);

  // Professional graph rendering
  const renderGraph = () => {
    let currentData;
    
    if (selectedSample) {
      // Use the selected sample directly
      currentData = selectedSample;
    } else {
      // Use allMockData - get the first sample from the selected collection
      const sampleCollection = allMockData[selectedData];
      if (sampleCollection && sampleCollection.fundamental && sampleCollection.fundamental.length > 0) {
        currentData = sampleCollection.fundamental[0];
      } else {
        return null;
      }
    }
    
    if (!currentData?.graphData?.nodes.length) return null;

    const width = 800;
    const height = 600;
    const centerX = width / 2;
    const centerY = height / 2;

    const filteredNodes = currentData.graphData.nodes.filter(node => {
      return filterSettings[node.type]?.visible !== false;
    });

    const filteredLinks = filterSettings.relationships?.visible !== false ? 
      currentData.graphData.links.filter(link => {
        const sourceId = link.source || link.from;
        const targetId = link.target || link.to;
        return filteredNodes.some(n => n.id === sourceId) && 
               filteredNodes.some(n => n.id === targetId);
      }) : [];

    const transformedNodes = filteredNodes.map(node => {
      const transformed = apply3DTransform(node.x, node.y, node.z || 0);
      return {
        ...node,
        transformedX: transformed.x,
        transformedY: transformed.y,
        transformedScale: transformed.scale,
        depth: transformed.depth
      };
    });

    const sortedNodes = transformedNodes.sort((a, b) => a.depth - b.depth);

    const getNodeColor = (type) => {
      switch (type) {
        case 'concepts': return '#3B82F6'; // Blue
        case 'formulas': return '#10B981'; // Emerald
        case 'values': return '#F59E0B'; // Amber
        case 'steps': return '#8B5CF6'; // Violet
        default: return '#6B7280'; // Gray
      }
    };

    const getNodeIcon = (type) => {
      switch (type) {
        case 'concepts': return '🧠';
        case 'formulas': return '📐';
        case 'values': return '🔢';
        case 'steps': return '✅';
        default: return '⭐';
      }
    };

    return (
      <g transform={`translate(${centerX + graphState.translation.x}, ${centerY + graphState.translation.y}) scale(${graphState.zoom})`}>
        {/* Links */}
        {filteredLinks.map((link, index) => {
          // Handle both 'source/target' and 'from/to' property names
          const sourceId = link.source || link.from;
          const targetId = link.target || link.to;
          
          const sourceNode = sortedNodes.find(n => n.id === sourceId);
          const targetNode = sortedNodes.find(n => n.id === targetId);
          if (!sourceNode || !targetNode) return null;

          const isHighlighted = graphState.selectedNode === sourceId || graphState.selectedNode === targetId;
          
          const dx = targetNode.transformedX - sourceNode.transformedX;
          const dy = targetNode.transformedY - sourceNode.transformedY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Safety check for zero distance to prevent division by zero
          if (distance === 0) {
            return null;
          }
          
          const normalizedDx = dx / distance;
          const normalizedDy = dy / distance;

          const sourceRadius = (sourceNode.radius || 20) * sourceNode.transformedScale * graphSettings.nodeSize;
          const targetRadius = (targetNode.radius || 20) * targetNode.transformedScale * graphSettings.nodeSize;

          const startX = sourceNode.transformedX + normalizedDx * sourceRadius;
          const startY = sourceNode.transformedY + normalizedDy * sourceRadius;
          const endX = targetNode.transformedX - normalizedDx * targetRadius;
          const endY = targetNode.transformedY - normalizedDy * targetRadius;

          // Additional safety check for NaN values
          if (isNaN(startX) || isNaN(startY) || isNaN(endX) || isNaN(endY)) {
            return null;
          }

          const avgDepth = (sourceNode.depth + targetNode.depth) / 2;
          const opacity = Math.max(0.3, 1 - Math.abs(avgDepth) / 200);

          return (
            <g key={`link-${index}`}>
                             <line
                 x1={startX} y1={startY} x2={endX} y2={endY}
                 stroke={isHighlighted ? "#3B82F6" : "#94A3B8"}
                 strokeWidth={(link.width || 2) * graphSettings.edgeThickness * Math.min(sourceNode.transformedScale, targetNode.transformedScale)}
                 strokeOpacity={opacity * (isHighlighted ? 0.9 : 0.6)}
                 markerEnd="url(#arrowhead)"
               />
              {graphSettings.showRelationshipLabels && distance > 80 && (
                <text
                  x={(startX + endX) / 2}
                  y={(startY + endY) / 2}
                  textAnchor="middle"
                  fontSize={10 * Math.min(sourceNode.transformedScale, targetNode.transformedScale)}
                  fill={isHighlighted ? "#1E40AF" : "#64748B"}
                  fillOpacity={opacity}
                  className="pointer-events-none select-none font-medium"
                >
                  {link.type}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {sortedNodes.map((node, index) => {
          const nodeRadius = (node.radius || 20) * node.transformedScale * graphSettings.nodeSize;
          const opacity = Math.max(0.4, 1 - Math.abs(node.depth) / 200);
          const isSelected = graphState.selectedNode === node.id;
          const isHovered = graphState.hoveredNode === node.id;
          
          return (
                         <g 
               key={node.id} 
               className="node-element cursor-pointer"
               style={{
                 opacity: opacity,
                 transform: `scale(${isSelected ? 1.3 : 1})`,
                 transition: 'all 0.3s ease'
               }}
             >
              <circle
                cx={node.transformedX}
                cy={node.transformedY}
                r={nodeRadius}
                fill={getNodeColor(node.type)}
                fillOpacity={opacity * (isSelected ? 1 : 0.8)}
                stroke={isSelected ? '#1D4ED8' : isHovered ? '#6366F1' : '#FFFFFF'}
                strokeWidth={isSelected ? 4 * node.transformedScale : 3 * node.transformedScale}
                className="transition-all duration-200"
                onClick={() => handleNodeClick(node)}
                onMouseEnter={() => handleNodeHover(node)}
                onMouseLeave={() => handleNodeHover(null)}
                style={{
                  filter: node.depth > 0 ? 'brightness(0.85)' : 'brightness(1.1)',
                  cursor: 'pointer'
                }}
              />
              
              {/* Node Icon */}
              <text
                x={node.transformedX}
                y={node.transformedY + 4}
                textAnchor="middle"
                fontSize={12 * node.transformedScale * graphSettings.nodeSize}
                className="pointer-events-none select-none"
              >
                {getNodeIcon(node.type)}
              </text>

              {graphSettings.showLabels && nodeRadius > 8 && (
                <text
                  x={node.transformedX}
                  y={node.transformedY + nodeRadius + 18 * node.transformedScale}
                  textAnchor="middle"
                  fontSize={11 * node.transformedScale}
                  fill="#1E293B"
                  fillOpacity={opacity}
                  className="pointer-events-none select-none font-semibold"
                >
                  {node.name}
                </text>
              )}
            </g>
          );
        })}

        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#3B82F6"
            />
          </marker>
        </defs>
      </g>
    );
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Data Selection */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Sample Data:</label>
            <select
              value={selectedData}
              onChange={(e) => setSelectedData(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="quadratic">Quadratic Equations</option>
              <option value="circleGeometry">Circle Geometry</option>
              <option value="factorRemainder">Factor & Remainder</option>
              <option value="probability">Probability</option>
              <option value="statistics">Statistics</option>
              <option value="trigonometry">Trigonometry</option>
            </select>
          </div>

          {/* View Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setGraphState(prev => ({ ...prev, isAutoRotating: !prev.isAutoRotating }))}
              className={`p-2 rounded-lg transition-colors ${
                graphState.isAutoRotating 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
              }`}
              title="Auto Rotate"
            >
              {graphState.isAutoRotating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>

            <button
              onClick={() => setGraphState(prev => ({ ...prev, zoom: Math.min(prev.zoom + 0.2, 3) }))}
              className="p-2 bg-indigo-100 hover:bg-indigo-200 rounded-lg transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4 text-indigo-700" />
            </button>

            <button
              onClick={() => setGraphState(prev => ({ ...prev, zoom: Math.max(prev.zoom - 0.2, 0.3) }))}
              className="p-2 bg-indigo-100 hover:bg-indigo-200 rounded-lg transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4 text-indigo-700" />
            </button>

            <button
              onClick={resetView}
              className="p-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
              title="Reset View"
            >
              <RotateCcw className="w-4 h-4 text-purple-700" />
            </button>
          </div>
        </div>

        {/* 3D Rotation Controls */}
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => rotateGraph('x', -15)}
            className="p-2 bg-white hover:bg-gray-50 shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
            title="Rotate Up (X-axis -15°)"
          >
            <ChevronUp className="w-5 h-5 text-indigo-700" />
          </button>

          <button
            onClick={() => rotateGraph('x', 15)}
            className="p-2 bg-white hover:bg-gray-50 shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
            title="Rotate Down (X-axis +15°)"
          >
            <ChevronDown className="w-5 h-5 text-indigo-700" />
          </button>

          <button
            onClick={() => rotateGraph('y', -15)}
            className="p-2 bg-white hover:bg-gray-50 shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
            title="Rotate Left (Y-axis -15°)"
          >
            <ChevronLeft className="w-5 h-5 text-indigo-700" />
          </button>

          <button
            onClick={() => rotateGraph('y', 15)}
            className="p-2 bg-white hover:bg-gray-50 shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
            title="Rotate Right (Y-axis +15°)"
          >
            <ChevronRight className="w-5 h-5 text-indigo-700" />
          </button>

          <button
            onClick={() => rotateGraph('z', -15)}
            className="p-2 bg-white hover:bg-gray-50 shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
            title="Rotate Counter-clockwise (Z-axis -15°)"
          >
            <RotateCw className="w-4 h-4 text-indigo-700" />
          </button>

                     <button
             onClick={() => rotateGraph('z', 15)}
             className="p-2 bg-white hover:bg-gray-50 shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
             title="Rotate Clockwise (Z-axis +15°)"
           >
             <RotateCw className="w-4 h-4 text-indigo-700" />
           </button>
        </div>
      </div>

      {/* Graph Visualization */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
            <Network className="w-5 h-5 text-indigo-600" />
            3D Knowledge Graph Visualization
          </h3>
          <div className="flex items-center gap-4 text-sm text-indigo-600">
            <div className="flex items-center gap-1">
              <Move className="w-4 h-4" />
              <span>Drag to pan</span>
            </div>
            <div className="flex items-center gap-1">
              <MousePointer className="w-4 h-4" />
              <span>Click nodes</span>
            </div>
                         <div className="flex items-center gap-1">
               <RotateCw className="w-4 h-4" />
               <span>Use controls to rotate</span>
             </div>
          </div>
        </div>
        
        <div className="relative border-2 border-indigo-300 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-100 to-cyan-100">
          <svg
            ref={svgRef}
            width="100%"
            height="600"
            viewBox="0 0 800 600"
            className="cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {renderGraph()}
          </svg>

          {/* Status Display */}
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 text-xs border border-indigo-200">
            <div className="font-semibold text-indigo-800 mb-1">View Status</div>
            <div className="space-y-1 text-indigo-600">
              <div>X: {graphState.rotation.x}° Y: {graphState.rotation.y}° Z: {graphState.rotation.z}°</div>
              <div>Zoom: {Math.round(graphState.zoom * 100)}%</div>
              {(() => {
                let currentData;
                if (selectedSample) {
                  currentData = selectedSample;
                } else {
                  const sampleCollection = allMockData[selectedData];
                  if (sampleCollection && sampleCollection.fundamental && sampleCollection.fundamental.length > 0) {
                    currentData = sampleCollection.fundamental[0];
                  }
                }
                return currentData?.graphData?.nodes ? (
                  <div>Entities: {currentData.graphData.nodes.length}</div>
                ) : null;
              })()}
              {graphState.isAutoRotating && (
                <div className="flex items-center gap-1 text-indigo-600">
                  <RotateCw className="w-3 h-3 animate-spin" />
                  Auto-rotating
                </div>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="absolute top-4 right-4 bg-white/95 rounded-lg p-3 text-xs border border-indigo-200">
            <div className="font-semibold text-indigo-800 mb-2">Entity Types</div>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <span>🧠</span>
                <span className="text-blue-600">Concepts</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>📐</span>
                <span className="text-emerald-600">Formulas</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>🔢</span>
                <span className="text-amber-600">Values</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>✅</span>
                <span className="text-violet-600">Steps</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraphVisualization; 